import React, { useMemo } from 'react';
import { orderBy } from 'lodash';
import ObservationTable from './components/ObservationTable';
import { updateAtmObservationAssignments } from '../../../../shared/utils/observationAssignmentsUtils';

const SECTION_DESCRIPTION = 'Assign the Observers to the following Candidate or Apprentice ATMs.';

/**
 * @typedef {object} ObservationAndCoachingEventAssignments
 * @property {number} externalUserId
 * @property {string} currentRoleName
 * @property {string} name
 * @property {boolean} [canBeObserved]
 * @property {boolean} [canObserve]
 */

/**
 * @typedef {object} ObservationAndCoachingAtmObservationAssignments
 * @property {number} [observerExternalUserId]
 * @property {number} [observedExternalUserId]
 */

/**
 * @typedef {object} ObservationAndCoachingProps
 * @property {ObservationAndCoachingEventAssignments[]} [eventAssignments]
 * @property {ObservationAndCoachingAtmObservationAssignments[]} [atmObservationAssignments]
 * @property {(eventFormData: Object | ((prev: Object) => Object)) => void} setEventFormData
 */

/**
 * @param {ObservationAndCoachingProps} props
 */
const ObservationAndCoaching = props => {
  const { eventAssignments = [], atmObservationAssignments = [], setEventFormData } = props;

  const { assignedObservees, assignedObserverIds, atmObservers } = useMemo(() => {
    const orderedAssignments = orderEventAssignments(eventAssignments);
    const observees = getAssignedObservees(orderedAssignments, atmObservationAssignments);
    return {
      assignedObservees: observees,
      assignedObserverIds: getAssignedObserverIds(observees),
      atmObservers: getAtmObservers(orderedAssignments)
    };
  }, [eventAssignments, atmObservationAssignments]);

  const setATMValue = (externalUserId, value) => {
    setEventFormData(prevFormData => ({
      ...prevFormData,
      atmObservationAssignments: updateAtmObservationAssignments(
        prevFormData.atmObservationAssignments,
        externalUserId,
        value
      )
    }));
  };

  return (
    <section className='page-section'>
      <div className='section-description'>
        <span className='section-title bold'>Observation and Coaching</span>
        <p>{SECTION_DESCRIPTION}</p>
      </div>
      <ObservationTable
        assignedObservees={assignedObservees}
        assignedObserverIds={assignedObserverIds}
        atmObservers={atmObservers}
        setATMValue={setATMValue}
      />
    </section>
  );
};

const orderEventAssignments = assignments =>
  orderBy(
    assignments,
    [user => user.currentRoleName, user => user.name.toLowerCase()],
    ['desc', 'asc']
  );

const getAssignedObservees = (assignments, atmObservationAssignments) =>
  assignments
    .filter(({ canBeObserved }) => canBeObserved)
    .map(({ externalUserId, currentRoleName, name }) => {
      const observationAssignment = atmObservationAssignments?.find(
        obs => obs.observedExternalUserId === externalUserId
      );

      return {
        externalUserId,
        currentRoleName,
        name,
        observerExternalUserId: observationAssignment?.observerExternalUserId
      };
    });

const getAssignedObserverIds = observees =>
  observees
    .filter(({ observerExternalUserId }) => observerExternalUserId)
    .map(({ observerExternalUserId }) => observerExternalUserId);

const getAtmObservers = assignments =>
  assignments
    .filter(({ canObserve }) => canObserve)
    .map(({ name, externalUserId }) => ({
      title: name,
      value: externalUserId
    }));

export default ObservationAndCoaching;
